import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { TenantState, tenantInitialState } from "./tenant.state";
import { TenantActions, loadTenantRequest, loadTenantSuccess, loadTenantFailure, clearTenantError } from "./tenant.actions";
import { tenantReducer } from "./tenant.reducer";
import { 
  selectTenant, 
  selectTenantLoading, 
  selectTenantError,
  selectTenantTitle
} from "./tenant.selectors";
import { TenantPublicInfo } from "../models/tenant.model";

@Injectable({
  providedIn: "root"
})
export class TenantStore {
  private state$ = new BehaviorSubject<TenantState>(tenantInitialState);

  // Public observables
  public tenant$ = this.state$.asObservable();
  public currentTenant$ = this.state$.asObservable().pipe(
    map(state => selectTenant(state))
  );
  public loading$ = this.state$.asObservable().pipe(
    map(state => selectTenantLoading(state))
  );
  public error$ = this.state$.asObservable().pipe(
    map(state => selectTenantError(state))
  );
  public tenantTitle$ = this.state$.asObservable().pipe(
    map(state => selectTenantTitle(state))
  );

  // Getters for current state
  get currentState(): TenantState {
    return this.state$.getValue();
  }

  get currentTenant() {
    return selectTenant(this.currentState);
  }

  get tenantTitle() {
    return selectTenantTitle(this.currentState);
  }

  // Action dispatchers
  dispatchLoadTenantRequest(): void {
    this.dispatch(loadTenantRequest());
  }

  dispatchLoadTenantSuccess(tenant: TenantPublicInfo): void {
    this.dispatch(loadTenantSuccess(tenant));
  }

  dispatchLoadTenantFailure(error: string): void {
    this.dispatch(loadTenantFailure(error));
  }

  dispatchClearError(): void {
    this.dispatch(clearTenantError());
  }

  // Private method to dispatch actions
  private dispatch(action: TenantActions): void {
    const currentState = this.state$.getValue();
    const newState = tenantReducer(currentState, action);
    this.state$.next(newState);
  }
} 